import React from 'react';
import { Printer, CheckCircle, Clock, AlertTriangle, X, ShieldCheck } from 'lucide-react';

export interface InvoiceItem {
  id?: string;
  description: string;
  sac_code?: string;
  qty: number;
  rate: number;
  amount: number;
}

export interface InvoiceData {
  id: number;
  invoice_number: string;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  customer_gstin?: string;
  invoice_date: string;
  due_date?: string;
  items: InvoiceItem[];
  subtotal: number;
  tax_type: string;
  tax_rate: number;
  tax_amount: number;
  discount: number;
  total_amount: number;
  notes?: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  rejection_reason?: string;
  created_by_name?: string;
  created_by_role?: string;
  approved_by_name?: string;
  approved_at?: string;
}

interface Props {
  invoice: InvoiceData | null;
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  onApprove?: (id: number) => void;
}

// Convert number to Indian words
function numberToWords(num: number): string {
  if (!num || isNaN(num)) return 'Zero Rupees Only';
  const a = [
    '', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ',
    'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const n = ('000000000' + Math.floor(num)).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (Number(n[1]) !== 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
  str += (Number(n[2]) !== 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
  str += (Number(n[3]) !== 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
  str += (Number(n[4]) !== 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
  str += (Number(n[5]) !== 0) ? ((str !== '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
  return 'Indian Rupees ' + str.trim() + ' Only';
}

export const InvoiceLetterheadModal: React.FC<Props> = ({
  invoice,
  isOpen,
  onClose,
  isAdmin,
  onApprove
}) => {
  if (!isOpen || !invoice) return null;

  const isApproved = invoice.status === 'approved';

  const handlePrint = () => {
    if (!isApproved) {
      alert('This bill is currently PENDING ADMIN APPROVAL. Final generation and official printing is allowed only after Admin approval.');
      return;
    }

    const letterheadEl = document.getElementById('printable-letterhead');
    if (!letterheadEl) {
      window.print();
      return;
    }

    // Clean up any existing print iframe
    const oldIframe = document.getElementById('letterhead-print-frame');
    if (oldIframe) {
      oldIframe.remove();
    }

    // Create an isolated invisible iframe
    const iframe = document.createElement('iframe');
    iframe.id = 'letterhead-print-frame';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const priDoc = iframe.contentWindow?.document || iframe.contentDocument;
    if (!priDoc) return;

    // Collect all stylesheets and inline styles from the host document
    let stylesHtml = '';
    document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
      stylesHtml += node.outerHTML;
    });

    const origTitle = document.title;
    const safeInvNum = invoice.invoice_number.replace(/[^a-zA-Z0-9_-]/g, '_');
    const invoicePdfTitle = `Tax_Invoice_${safeInvNum}`;

    priDoc.open();
    priDoc.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <title>${invoicePdfTitle}</title>
          ${stylesHtml}
          <style>
            @page {
              size: A4 portrait;
              margin: 6mm 6mm 6mm 6mm;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
              background-color: #ffffff !important;
              color: #0f172a !important;
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            #print-wrapper {
              width: 100% !important;
              max-width: 210mm !important;
              min-height: 280mm !important;
              margin: 0 auto !important;
              padding: 0 !important;
              box-sizing: border-box !important;
              background: #ffffff !important;
            }
            #printable-letterhead {
              width: 100% !important;
              margin: 0 !important;
              padding: 16px 24px !important;
              box-shadow: none !important;
              border: none !important;
              border-radius: 0 !important;
              background: #ffffff !important;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              box-sizing: border-box !important;
            }
            .no-print {
              display: none !important;
            }
          </style>
        </head>
        <body>
          <div id="print-wrapper">
            ${letterheadEl.outerHTML}
          </div>
        </body>
      </html>
    `);
    priDoc.close();

    let isDonePrinting = false;
    const executePrint = () => {
      if (isDonePrinting) return;
      isDonePrinting = true;
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (err) {
        console.error('Iframe print error:', err);
        window.print();
      } finally {
        setTimeout(() => {
          document.title = origTitle;
        }, 1000);
      }
    };

    if (iframe.contentWindow) {
      iframe.contentWindow.onload = () => {
        setTimeout(executePrint, 300);
      };
    }
    setTimeout(executePrint, 600);
  };

  const isCgstSgst = invoice.tax_type === 'cgst_sgst_18' || invoice.tax_type === 'intra_state';
  const halfTaxRate = (invoice.tax_rate || 18) / 2;
  const halfTaxAmount = (invoice.tax_amount || 0) / 2;

  return (
    <div className="letterhead-modal-overlay fixed inset-0 z-50 overflow-y-auto bg-black/60 flex items-center justify-center p-2 sm:p-4 print:p-0 print:bg-white print:static print:inset-auto">
      {/* Print Specific CSS */}
      <style>{`
        @media print {
          html, body {
            background: #ffffff !important;
            background-color: #ffffff !important;
            color: #0f172a !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            height: auto !important;
          }
          .no-print {
            display: none !important;
          }
          .letterhead-modal-overlay {
            position: static !important;
            background: #ffffff !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
            height: auto !important;
            display: block !important;
            width: 100% !important;
          }
          .letterhead-modal-card {
            position: static !important;
            max-width: 100% !important;
            max-height: none !important;
            overflow: visible !important;
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .letterhead-scroll-body {
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
            padding: 0 !important;
            margin: 0 !important;
            background: #ffffff !important;
          }
          #printable-letterhead {
            position: static !important;
            width: 100% !important;
            max-width: 210mm !important;
            min-height: 285mm !important;
            margin: 0 auto !important;
            padding: 15px 25px !important;
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            background: #ffffff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: A4 portrait;
            margin: 6mm 6mm 6mm 6mm;
          }
        }
      `}</style>

      {/* Main Container */}
      <div className="letterhead-modal-card relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[96vh] flex flex-col overflow-hidden print:max-w-none print:max-h-none print:shadow-none print:rounded-none">
        
        {/* Top Floating Control Bar (Hidden in Print) */}
        <div className="no-print bg-slate-900 text-white px-6 py-3 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-3">
            <span className="font-bold text-sm tracking-wide text-emerald-400">
              OFFICIAL BILL PREVIEW
            </span>
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold uppercase flex items-center gap-1.5 ${
              isApproved ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' :
              invoice.status === 'rejected' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
              'bg-amber-500/20 text-amber-300 border border-amber-500/30'
            }`}>
              {isApproved ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
              {isApproved ? 'Approved by Admin' : invoice.status === 'rejected' ? 'Rejected' : 'Pending Admin Approval'}
            </span>
          </div>

          <div className="flex items-center gap-2.5">
            {isAdmin && !isApproved && invoice.status !== 'rejected' && onApprove && (
              <button
                onClick={() => onApprove(invoice.id)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow transition-colors"
              >
                <ShieldCheck className="w-4 h-4" />
                Approve & Authorize Bill
              </button>
            )}

            <button
              onClick={handlePrint}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow transition-colors ${
                isApproved 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer' 
                  : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }`}
              title={isApproved ? "Print or Save as PDF" : "Requires Admin Approval to print final bill"}
            >
              <Printer className="w-4 h-4" />
              Print / Save PDF
            </button>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Status Notice Banner if Pending */}
        {!isApproved && (
          <div className="no-print bg-amber-50 border-b border-amber-200 px-6 py-2 text-xs text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span>
              <strong>Draft Notice:</strong> This invoice is pending Admin approval. Final generation, stamping, and official printing will be unlocked once approved by an Administrator.
            </span>
          </div>
        )}

        {/* Printable Document Body (A4 Letterhead Format) */}
        <div className="letterhead-scroll-body overflow-y-auto flex-1 p-3 sm:p-6 bg-slate-100 print:bg-white print:p-0">
          <div
            id="printable-letterhead"
            className="relative bg-white text-slate-800 mx-auto w-full max-w-[210mm] min-h-[297mm] shadow-lg print:shadow-none p-6 sm:p-10 flex flex-col justify-between border border-gray-200 print:border-none"
            style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif" }}
          >
            {/* Watermark for Pending Approval */}
            {!isApproved && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none z-10 overflow-hidden">
                <span className="text-red-500/10 font-black text-5xl sm:text-7xl -rotate-45 tracking-widest border-8 border-red-500/10 px-8 py-4 rounded-3xl uppercase text-center">
                  PENDING ADMIN APPROVAL<br />
                  <span className="text-3xl sm:text-4xl font-bold">DRAFT - NOT OFFICIAL</span>
                </span>
              </div>
            )}

            <div>
              {/* ======================================================== */}
              {/* 1. TOP BRANDED LETTERHEAD HEADER (MATCHES SAMPLE PDF)    */}
              {/* ======================================================== */}
              <div className="relative mb-6">
                {/* Wavy Decorative Curves Matching PDF */}
                <div className="w-full h-10 overflow-hidden -mx-6 sm:-mx-10 -mt-6 sm:-mt-10 mb-4 print:-mx-0 print:-mt-0">
                  <svg viewBox="0 0 800 60" preserveAspectRatio="none" className="w-full h-full">
                    <path
                      d="M0,0 L800,0 L800,30 Q600,60 400,20 T0,40 Z"
                      fill="#84cc16"
                      opacity="0.7"
                    />
                    <path
                      d="M0,0 L800,0 L800,20 Q550,55 350,15 T0,25 Z"
                      fill="#16a34a"
                      opacity="0.9"
                    />
                    <path
                      d="M0,0 L800,0 L800,12 Q500,40 250,10 T0,18 Z"
                      fill="#15803d"
                    />
                  </svg>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-3 border-b-2 border-[#15803d]">
                  {/* Left: Logo Only */}
                  <div className="flex items-center">
                    <img
                      src="/logofull.webp"
                      alt="X2 InfoTech Logo"
                      className="h-14 sm:h-16 object-contain"
                    />
                  </div>

                  {/* Right: Registered Company Details (from Letterhead PDF) */}
                  <div className="text-left sm:text-right max-w-md">
                    <h2 className="text-xs sm:text-sm font-extrabold text-[#15803d] uppercase tracking-tight leading-tight">
                      X2 INFOTECH CONSULTANCY IT SOLUTION PRIVATE LIMITED
                    </h2>
                    <p className="text-[9px] sm:text-[10px] font-semibold text-gray-700 leading-snug mt-1">
                      PINTO PARK, GOLA KA MANDIR, GWALIOR, MADHYA PRADESH, INDIA, 474005
                    </p>
                    <div className="text-[9px] sm:text-[10px] font-bold text-[#15803d] mt-1 flex flex-wrap sm:justify-end gap-x-3">
                      <span>CIN: U62099MP2025PTC079777</span>
                      <span>GST: U23AAACX5974H1Z4</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ======================================================== */}
              {/* 2. INVOICE TITLE & METADATA BAR                          */}
              {/* ======================================================== */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-6">
                <div>
                  <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                    TAX INVOICE
                  </h1>
                  <p className="text-xs text-slate-500">Original for Recipient</p>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-sm font-black text-[#15803d]">
                    INVOICE NO: {invoice.invoice_number}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    <strong>Invoice Date:</strong> {new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  {invoice.due_date && (
                    <div className="text-xs text-slate-600">
                      <strong>Due Date:</strong> {new Date(invoice.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </div>
              </div>

              {/* ======================================================== */}
              {/* 3. BILL TO / CLIENT DETAILS                              */}
              {/* ======================================================== */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200 mb-6 text-xs">
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                    BILLED TO:
                  </div>
                  <div className="font-bold text-slate-900 text-sm">{invoice.customer_name}</div>
                  {invoice.customer_address && (
                    <div className="text-slate-600 whitespace-pre-line mt-1">
                      {invoice.customer_address}
                    </div>
                  )}
                  {invoice.customer_phone && (
                    <div className="text-slate-600 mt-1">
                      <span className="font-semibold">Phone:</span> {invoice.customer_phone}
                    </div>
                  )}
                  {invoice.customer_email && (
                    <div className="text-slate-600">
                      <span className="font-semibold">Email:</span> {invoice.customer_email}
                    </div>
                  )}
                  {invoice.customer_gstin && (
                    <div className="text-slate-800 font-bold mt-1">
                      GSTIN: {invoice.customer_gstin}
                    </div>
                  )}
                </div>

                <div className="sm:text-right flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                      SUPPLIER / ISSUER:
                    </div>
                    <div className="font-bold text-slate-900">X2 InfoTech Consultancy IT Solution Pvt. Ltd.</div>
                    <div className="text-slate-600">State: Madhya Pradesh (Code: 23)</div>
                    <div className="text-slate-600">Place of Supply: Madhya Pradesh (23)</div>
                  </div>
                  <div className="mt-2 text-slate-500 text-[11px]">
                    Created by: <span className="font-semibold text-slate-700">{invoice.created_by_name || 'Staff'}</span> ({invoice.created_by_role?.toUpperCase() || 'FINANCE'})
                    {isApproved && (
                      <div className="text-emerald-700 font-semibold mt-0.5">
                        ✓ Approved by Admin{invoice.approved_by_name && invoice.approved_by_name !== '0' && invoice.approved_by_name !== '1' ? `: ${invoice.approved_by_name}` : ''}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ======================================================== */}
              {/* 4. ITEMISED SERVICES TABLE                               */}
              {/* ======================================================== */}
              <div className="border border-slate-200 rounded-xl overflow-hidden mb-6">
                <table className="w-full text-xs text-left">
                  <thead className="bg-[#15803d] text-white uppercase text-[10px] tracking-wider font-bold">
                    <tr>
                      <th className="py-2.5 px-3 w-10 text-center">#</th>
                      <th className="py-2.5 px-4">Description of Services</th>
                      <th className="py-2.5 px-3 w-20 text-center">SAC Code</th>
                      <th className="py-2.5 px-3 w-16 text-right">Qty</th>
                      <th className="py-2.5 px-4 w-28 text-right">Rate (₹)</th>
                      <th className="py-2.5 px-4 w-28 text-right">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {invoice.items && invoice.items.length > 0 ? (
                      invoice.items.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50">
                          <td className="py-2.5 px-3 text-center text-slate-500 font-medium">{idx + 1}</td>
                          <td className="py-2.5 px-4 font-semibold text-slate-800">{item.description}</td>
                          <td className="py-2.5 px-3 text-center text-slate-500">{item.sac_code || '998314'}</td>
                          <td className="py-2.5 px-3 text-right text-slate-700">{item.qty}</td>
                          <td className="py-2.5 px-4 text-right text-slate-700">₹{Number(item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td className="py-2.5 px-4 text-right font-bold text-slate-900">₹{Number(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-4 text-center text-slate-400">No items listed</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* ======================================================== */}
              {/* 5. TAX CALCULATIONS & TOTALS BREAKDOWN                   */}
              {/* ======================================================== */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                {/* Left: Notes & Bank Info */}
                <div className="text-xs space-y-3">
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                      AMOUNT IN WORDS:
                    </div>
                    <div className="font-semibold text-slate-800 bg-slate-50 p-2.5 rounded-lg border border-slate-200 italic">
                      {numberToWords(invoice.total_amount)}
                    </div>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1 text-[11px]">
                    <div className="font-bold text-slate-800 uppercase tracking-wide text-[10px]">
                      Bank & Payment Details
                    </div>
                    <div><span className="font-semibold text-slate-600">Beneficiary:</span> X2 INFOTECH CONSULTANCY IT SOLUTION PRIVATE LIMITED</div>
                    <div><span className="font-semibold text-slate-600">Bank Name:</span> Axis Bank</div>
                    <div><span className="font-semibold text-slate-600">Account No:</span> 926020002404126</div>
                    <div><span className="font-semibold text-slate-600">IFSC Code:</span> UTIB0002532</div>
                    <div><span className="font-semibold text-slate-600">UPI ID:</span> </div>
                  </div>

                  {invoice.notes && (
                    <div className="text-slate-500 text-[11px]">
                      <strong>Terms & Notes:</strong> {invoice.notes}
                    </div>
                  )}
                </div>

                {/* Right: Calculations Summary */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs space-y-2">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal:</span>
                    <span className="font-semibold text-slate-900">₹{Number(invoice.subtotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>

                  {Number(invoice.discount) > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Discount:</span>
                      <span className="font-semibold">-₹{Number(invoice.discount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}

                  {/* Taxes Breakdown */}
                  {isCgstSgst ? (
                    <>
                      <div className="flex justify-between text-slate-600">
                        <span>CGST ({halfTaxRate}%):</span>
                        <span className="font-semibold text-slate-900">₹{halfTaxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>SGST ({halfTaxRate}%):</span>
                        <span className="font-semibold text-slate-900">₹{halfTaxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-slate-600">
                      <span>IGST ({invoice.tax_rate}%):</span>
                      <span className="font-semibold text-slate-900">₹{Number(invoice.tax_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}

                  <div className="border-t-2 border-slate-300 pt-2.5 mt-2 flex justify-between items-center">
                    <span className="text-sm font-extrabold text-slate-900">Total Bill Amount:</span>
                    <span className="text-lg font-black text-[#15803d]">
                      ₹{Number(invoice.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* ======================================================== */}
              {/* 6. SIGNATORY BLOCK                                       */}
              {/* ======================================================== */}
              <div className="flex justify-between items-end mt-8 pt-4 border-t border-slate-200">
                <div className="text-[10px] text-slate-400 max-w-xs leading-tight">
                  This is a computer-generated tax invoice. Authenticity can be verified at info@x2infotech.in.
                </div>
                <div className="text-center">
                  <div className="text-xs font-bold text-slate-800 mb-10">
                    For X2 INFOTECH CONSULTANCY IT SOLUTION PVT. LTD.
                  </div>
                  <div className="border-t border-slate-400 w-48 mx-auto"></div>
                  <div className="text-[11px] font-semibold text-slate-600 mt-1">
                    Authorized Signatory
                  </div>
                </div>
              </div>
            </div>

            {/* ======================================================== */}
            {/* 7. BOTTOM GREEN CAPSULE FOOTER (MATCHES SAMPLE PDF)      */}
            {/* ======================================================== */}
            <div className="mt-8 pt-4">
              <div className="bg-[#15803d] text-white rounded-full px-4 py-2.5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[10px] sm:text-xs font-semibold shadow-md">
                <div className="flex items-center gap-1.5">
                  <span>🌐</span>
                  <span>www.x2infotech.in</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span>📞</span>
                  <span>+91 90741 40432</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span>✉️</span>
                  <span>info@x2infotech.in</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};
